
import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { UserForm } from "@/components/users/UserForm";
import { toast } from "sonner";
import { LockIcon, UsersIcon, AlertTriangleIcon, DatabaseIcon, RefreshCwIcon } from "lucide-react";
import { getUsers } from "@/services/UserService";
import { User } from "@/types";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { handleSaveOperation } from "@/utils/saveUtils";

const PermissoesTabContent = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [storageInfo, setStorageInfo] = useState<string>("");
  const navigate = useNavigate();
  
  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Simular uma operação assíncrona para tornar a experiência mais realista
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const usersList = getUsers();
      setUsers(usersList);
      
      // Verificar onde os dados estão sendo armazenados
      const storageType = "localStorage";
      setStorageInfo(`Os dados de usuários estão sendo armazenados em ${storageType}. Total de usuários: ${usersList.length}`);
      
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
      setError("Erro ao carregar dados de usuários");
      toast.error("Erro ao carregar dados de usuários");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    // Carregar usuários ao montar o componente
    loadUsers();
  }, []);

  const handleRedirectToUsers = () => {
    navigate("/usuarios");
  };

  const handleUserUpdateSuccess = async () => {
    const success = await handleSaveOperation(
      async () => {
        // Aqui seria a operação de salvamento real
        await new Promise(resolve => setTimeout(resolve, 800)); // Simulação
        return true;
      },
      "Permissões atualizadas com sucesso!",
      "Erro ao atualizar permissões"
    );
    
    if (success) {
      // Recarregar a lista de usuários
      loadUsers();
    }
  };

  return (
    <Card className="mb-6 shadow-sm border-gray-200 overflow-hidden hover:shadow-md transition-all bg-white">
      <CardHeader className="pb-4 border-b border-gray-100 bg-white">
        <div className="flex items-center mb-2">
          <div className="h-8 w-8 rounded-md bg-blue-100 flex items-center justify-center mr-2">
            <LockIcon className="h-4 w-4 text-blue-600" />
          </div>
          <CardTitle className="text-xl font-semibold text-gray-800">Permissões de Usuários</CardTitle>
        </div>
        <CardDescription className="text-gray-500">
          Configure quais usuários possuem acesso ao módulo financeiro e seus níveis de permissão
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 bg-white">
        {storageInfo && (
          <div className="mb-4 p-3 bg-indigo-50 border border-indigo-100 rounded-md flex items-start">
            <DatabaseIcon className="h-5 w-5 text-indigo-500 mt-0.5 mr-2 flex-shrink-0" />
            <p className="text-sm text-indigo-700">{storageInfo}</p>
          </div>
        )}
        
        <div className="flex justify-end mb-4">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={loadUsers} 
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCwIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar Lista
          </Button>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        ) : error ? (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangleIcon className="h-4 w-4" />
            <AlertTitle>Erro ao carregar usuários</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
            <div className="mt-4">
              <Button onClick={loadUsers} variant="outline">
                Tentar novamente
              </Button>
            </div>
          </Alert>
        ) : (
          <>
            {users.length === 0 ? (
              <Alert variant="destructive" className="mb-6">
                <AlertTriangleIcon className="h-4 w-4" />
                <AlertTitle>Nenhum usuário encontrado</AlertTitle>
                <AlertDescription>
                  Não há usuários cadastrados no sistema. Isso pode ocorrer se a inicialização do administrador padrão não funcionou corretamente.
                </AlertDescription>
                <div className="mt-4">
                  <Button onClick={handleRedirectToUsers} variant="outline">
                    Ir para Gerenciamento de Usuários
                  </Button>
                </div>
              </Alert>
            ) : (
              <>
                <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-md flex items-start">
                  <UsersIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-sm text-blue-700">
                    Usuários com permissão de administrador terão acesso completo a todas as funcionalidades financeiras. 
                    Configure com cuidado para garantir a segurança dos dados financeiros.
                  </p>
                </div>
                <div className="mb-4 p-3 bg-green-50 border border-green-100 rounded-md">
                  <h3 className="font-medium text-green-800 mb-2">Usuários no Sistema: {users.length}</h3>
                  <ul className="text-sm text-green-700 space-y-1 pl-2">
                    {users.map((user, index) => (
                      <li key={user.id}>
                        {index + 1}. {user.nome} ({user.email}) - {user.role}
                      </li>
                    ))}
                  </ul>
                </div>
                <UserForm 
                  onSuccess={handleUserUpdateSuccess}
                  onCancel={() => {}}
                  readOnlyPermissions={false}
                />
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PermissoesTabContent;
